import React, { useCallback, useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { ReactComponent as CloseIcon } from '@pkg/assets/icons/v2/close.svg';
import { CircleButton, ItemCount } from '@pkg/components';
import { ArtifactDetailMobile, ArtifactTile } from '@pkg/components/Artifacts/Mobile';
import { useSearchParamsState } from '@pkg/hooks/navigation/useSearchParams';

import { userArtifacts } from '@app/store/selectors/artifacts/artifacts';
import { usersOwnedArtifactIds } from '@app/store/selectors/artifacts/artifacts-metadata';

import { Typography } from '../../../DesignSystem';

import './ArtifactsList.scss';

const bgSrc = '/assets/img/artifacts/backdrops/prismcase_baked.jpg';

export function ArtifactsListMobile() {
    const { t } = useTranslation(['artifacts']);
    const { collectedKeys, uncollectedKeys } = useSelector(userArtifacts);
    const totalIds = useSelector(usersOwnedArtifactIds);
    const { params, removeParam } = useSearchParamsState<{ goBackEnabled?: 'string' }>();

    const [enableGoBack, setGoBackEnabled] = useState(false);
    useEffect(() => {
        if (params.goBackEnabled) {
            setGoBackEnabled(true);
            const timeout = setTimeout(() => {
                removeParam('goBackEnabled');
            }, 500);
            return () => clearTimeout(timeout);
        }
    }, [params.goBackEnabled, removeParam]);

    const navigate = useNavigate();

    const handleClose = useCallback(() => {
        if (enableGoBack) {
            navigate(-1);
            return;
        }
        navigate('/profile');
    }, [enableGoBack, navigate]);

    return (
        <div className="yat-artifacts-list" style={{ backgroundImage: `url(${bgSrc})` }}>
            <div className="yat-artifacts-list__close">
                <CircleButton ariaLabel={'close'} size="large" onClick={handleClose}>
                    <CloseIcon />
                </CircleButton>
            </div>
            {collectedKeys.length > 0 && (
                <div className="yat-artifacts-list__title">
                    <Typography type="headline" weight="700">
                        {t('my_collection')}
                    </Typography>
                    <ItemCount count={totalIds?.length} />
                </div>
            )}
            <div className="yat-artifacts-list__grid">
                {collectedKeys?.map((currentArtifactType) => {
                    return (
                        <div key={currentArtifactType} className="yat-artifacts-list__grid-item">
                            <ArtifactTile type={currentArtifactType}>
                                <ArtifactDetailMobile artifactType={currentArtifactType} />
                            </ArtifactTile>
                        </div>
                    );
                })}
            </div>

            {uncollectedKeys?.length > 0 && (
                <div className="yat-artifacts-list__title">
                    <Typography type="headline" weight="700">
                        {t('not_collected')}
                    </Typography>
                </div>
            )}
            <div className="yat-artifacts-list__grid">
                {uncollectedKeys.map((currentArtifactType) => {
                    return (
                        <div key={currentArtifactType} className="yat-artifacts-list__grid-item">
                            <ArtifactTile type={currentArtifactType}>
                                <ArtifactDetailMobile artifactType={currentArtifactType} />
                            </ArtifactTile>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
